import React, { useCallback, useEffect, useState } from "react";
import { Tag, Plus, Pause, Play, Trash2, Loader2, Copy, Check } from "lucide-react";
import api from "../../api";
import { useToast } from "../../hooks/use-toast";

const money = (n) => `$${Number(n || 0) % 1 === 0 ? Number(n).toFixed(0) : Number(n).toFixed(2)}`;
const expiryLabel = (iso) => {
  const days = Math.ceil((new Date(iso) - Date.now()) / 86400000);
  return days <= 1 ? "expires today" : `expires in ${days} days`;
};

export const AdminDiscounts = () => {
  const { toast } = useToast();
  const [codes, setCodes] = useState([]);
  const [packages, setPackages] = useState([]);
  const [uses, setUses] = useState([]);
  const [busy, setBusy] = useState("");
  const [copied, setCopied] = useState("");
  const [form, setForm] = useState({ code: "", type: "percent", value: 50, packages: [], maxRedemptions: 0, oncePerBusiness: true, expiresAt: "", note: "" });

  const load = useCallback(async () => {
    try {
      const [c, p, r] = await Promise.all([
        api.get("/admin/discounts"), api.get("/admin/packages"), api.get("/admin/discounts/redemptions"),
      ]);
      setCodes(c.data); setPackages(p.data); setUses(r.data);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setBusy("create");
    try {
      const { data } = await api.post("/admin/discounts", { ...form, value: Number(form.value), maxRedemptions: Number(form.maxRedemptions) });
      toast({ title: `Code ${data.code} created` });
      setForm({ code: "", type: "percent", value: 50, packages: [], maxRedemptions: 0, oncePerBusiness: true, expiresAt: "", note: "" });
      await load();
    } catch (e) { toast({ title: "Could not create the code", description: e.response?.data?.detail }); }
    finally { setBusy(""); }
  };

  const toggle = async (c) => {
    setBusy(c.id);
    try { await api.post(`/admin/discounts/${c.id}/toggle`); await load(); }
    catch { toast({ title: "Action failed" }); }
    finally { setBusy(""); }
  };

  const remove = async (c) => {
    if (c.redeemed > 0 && !window.confirm(`${c.code} has ${c.redeemed} redemption(s). Delete it anyway?`)) return;
    setBusy(c.id);
    try { await api.delete(`/admin/discounts/${c.id}`); toast({ title: `${c.code} deleted` }); await load(); }
    catch { toast({ title: "Action failed" }); }
    finally { setBusy(""); }
  };

  const copy = (code) => {
    navigator.clipboard?.writeText(code);
    setCopied(code);
    setTimeout(() => setCopied(""), 1500);
  };

  const togglePkg = (id) => setForm((f) => ({
    ...f, packages: f.packages.includes(id) ? f.packages.filter((x) => x !== id) : [...f.packages, id],
  }));

  const input = "bg-secondary rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#16a34a]/40";

  const clearCoupons = async () => {
    if (!window.confirm("Delete every discount code and its redemption history? This can't be undone.")) return;
    try {
      const { data } = await api.post("/admin/discounts/clear", { confirm: "CLEAR" });
      toast({ title: `Cleared ${data.codes} code(s)`, description: `${data.redemptions} redemption record(s) removed.` });
      load();
    } catch (e) { toast({ title: e?.response?.data?.detail || "Could not clear that" }); }
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-5 space-y-5" data-testid="admin-discounts">
      <div>
        <h2 className="font-bold text-foreground flex items-center gap-2"><Tag size={18} className="text-[#16a34a]" /> Discount codes</h2>
        <p className="text-xs text-muted-foreground mt-0.5">Codes shops type in at checkout. The discount is applied to our own price, so it can't be faked. Leave the packages empty to let a code work on everything, and 100% off skips Stripe and grants it free.</p>
        <button onClick={clearCoupons} data-testid="coupons-clear-btn"
          className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-rose-600 border border-rose-500/40 hover:bg-rose-500/10 rounded-xl px-3 py-2 transition-colors">
          <Trash2 size={13} /> Clear all codes and their history
        </button>
      </div>

      <div className="border border-border rounded-xl p-4 space-y-3">
        <div className="flex flex-wrap gap-2">
          <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
            placeholder="CODE (blank = generate)" data-testid="discount-code-input" className={`${input} uppercase w-[220px]`} />
          <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="discount-type" className={input}>
            <option value="percent">% off</option>
            <option value="amount">$ off</option>
          </select>
          <input type="number" min="1" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })}
            data-testid="discount-value" className={`${input} w-[100px]`} />
          <input type="number" min="0" value={form.maxRedemptions} onChange={(e) => setForm({ ...form, maxRedemptions: e.target.value })}
            placeholder="Max uses (0 = ∞)" title="Total redemptions cap — 0 means unlimited"
            data-testid="discount-max" className={`${input} w-[150px]`} />
          <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
            placeholder="Note (who it's for)" data-testid="discount-note" className={`${input} flex-1 min-w-[180px]`} />
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2 text-xs font-semibold text-foreground cursor-pointer">
            <input type="checkbox" checked={form.oncePerBusiness} onChange={(e) => setForm({ ...form, oncePerBusiness: e.target.checked })}
              data-testid="discount-once" className="h-4 w-4 accent-[#16a34a]" />
            One use per business
          </label>
          <label className="flex items-center gap-2 text-xs font-semibold text-foreground">
            Expires
            <input type="date" value={form.expiresAt} onChange={(e) => setForm({ ...form, expiresAt: e.target.value })}
              data-testid="discount-expires" className={input} />
            <span className="font-normal text-muted-foreground">blank = never</span>
          </label>
        </div>
        <div>
          <p className="text-xs font-semibold text-muted-foreground mb-1.5">Applies to {form.packages.length === 0 ? "everything" : `${form.packages.length} package(s)`}</p>
          <div className="flex flex-wrap gap-1.5">
            {packages.map((p) => (
              <button key={p.id} onClick={() => togglePkg(p.id)} data-testid={`discount-pkg-${p.id}`}
                className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-colors ${form.packages.includes(p.id) ? "border-[#16a34a] bg-[#e7f6ec] dark:bg-[#16341f] text-[#16a34a]" : "border-border text-muted-foreground hover:bg-secondary"}`}>
                {p.label} · {money(p.amount)}
              </button>
            ))}
          </div>
        </div>
        <button onClick={create} disabled={busy === "create"} data-testid="discount-create-btn"
          className="inline-flex items-center gap-1.5 text-xs font-bold bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-60 text-white rounded-xl px-4 py-2.5 transition-colors">
          {busy === "create" ? <Loader2 className="animate-spin" size={13} /> : <Plus size={13} />} Create code
        </button>
      </div>

      <div className="space-y-2">
        {codes.length === 0 && <p className="text-sm text-muted-foreground" data-testid="discounts-empty">No codes yet.</p>}
        {codes.map((c) => (
          <div key={c.id} className="border border-border rounded-xl p-3 flex flex-wrap items-center gap-3" data-testid={`discount-row-${c.code}`}>
            <div className="flex-1 min-w-[200px]">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-extrabold text-foreground tracking-wide">{c.code}</span>
                <button onClick={() => copy(c.code)} data-testid={`discount-copy-${c.code}`} title="Copy" className="text-muted-foreground hover:text-foreground">
                  {copied === c.code ? <Check size={13} className="text-[#16a34a]" /> : <Copy size={13} />}
                </button>
                <span className="text-[11px] font-bold bg-secondary text-muted-foreground px-2 py-0.5 rounded-full">
                  {c.type === "percent" ? `${c.value}% off` : `${money(c.value)} off`}
                </span>
                {!c.active && <span className="text-[11px] font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Paused</span>}
                {c.expired && <span className="text-[11px] font-bold bg-rose-100 text-rose-700 px-2 py-0.5 rounded-full" data-testid={`discount-expired-${c.code}`}>Expired</span>}
              </div>
              <p className="text-xs text-muted-foreground mt-1" data-testid={`discount-usage-${c.code}`}>
                {c.redeemed} used{c.maxRedemptions ? ` of ${c.maxRedemptions}` : ""} · {c.packages.length === 0 ? "any purchase" : c.packages.join(", ")}
                {c.oncePerBusiness ? " · one per shop" : " · reusable by the same shop"}
                {c.expiresAt && !c.expired ? ` · ${expiryLabel(c.expiresAt)}` : ""}
                {c.note ? ` · ${c.note}` : ""}
              </p>
            </div>
            <button onClick={() => toggle(c)} disabled={busy === c.id} data-testid={`discount-toggle-${c.code}`}
              className="inline-flex items-center gap-1.5 text-xs font-semibold border border-border hover:bg-secondary rounded-xl px-3 py-2 transition-colors">
              {busy === c.id ? <Loader2 className="animate-spin" size={13} /> : c.active ? <Pause size={13} /> : <Play size={13} />}
              {c.active ? "Pause" : "Resume"}
            </button>
            <button onClick={() => remove(c)} data-testid={`discount-delete-${c.code}`}
              className="h-9 w-9 rounded-xl border border-border flex items-center justify-center text-muted-foreground hover:text-rose-500 hover:border-rose-300 transition-colors"><Trash2 size={15} /></button>
          </div>
        ))}
      </div>

      {uses.length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-foreground mb-2">Redemptions ({uses.length})</h3>
          <div className="space-y-1.5" data-testid="discount-redemptions">
            {uses.slice(0, 20).map((u) => (
              <p key={u.id} className="text-xs text-muted-foreground">
                <span className="font-bold text-foreground">{u.code}</span> · {u.businessName || u.email} · {u.packageId} · paid {money(u.paidAmount)} of {money(u.listAmount)} · {new Date(u.at).toLocaleDateString()}
              </p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDiscounts;
